// src/new/components/monitor/TriageChat/TriageLinkLanding.tsx
import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import { ROUTE_FLOWS } from 'new/constants/pathnames';
import { useTriageLauncher } from './TriageChatProvider';
import { buildTriageContextFromParams, integrateUrlForContext } from './triageContext';

const CANONICAL_KEYS = [
  'resource_type',
  'resource_id',
  'run_id',
  'error_message',
  'timestamp',
  'flow_id',
];
const LEGACY_KEYS = ['type', 'id', 'runId', 'err', 'ts'];

/** Which param vocabulary the link used — lets us watch the legacy→canonical
 *  migration and spot senders still on the old names. */
function paramStyle(sp: URLSearchParams): 'canonical' | 'legacy' | 'mixed' | 'none' {
  const hasCanonical = CANONICAL_KEYS.some((k) => sp.has(k));
  const hasLegacy = LEGACY_KEYS.some((k) => sp.has(k));
  if (hasCanonical && hasLegacy) return 'mixed';
  if (hasCanonical) return 'canonical';
  if (hasLegacy) return 'legacy';
  return 'none';
}

/**
 * Landing handler for the routing-agnostic triage deep link
 * (`/monitor/triage?resource_type=flows&resource_id=531425&run_id=…&error_message=…&timestamp=…`).
 *
 * Its single job: translate the link's context params into a TriageContext,
 * open the app-root triage modal, and redirect to the Integrate page with the
 * flow selected. Because the modal is owned by `TriageChatProvider` — an
 * ancestor that survives this navigation — opening it here leaves it mounted
 * over the Integrate canvas the user lands on.
 *
 * Keeping this translation in ONE place is what makes the emailed link stable:
 * future routing/behavior changes only touch this file, never the sent link.
 *
 * Observability: every landing emits `triage_deeplink_landed` (PostHog) with the
 * parse outcome and which data points the link carried, so the funnel
 * alert → click → context → modal is measurable end to end and a silent parse
 * failure can't masquerade as "nobody clicked".
 */
export const TriageLinkLanding: React.FC = () => {
  const history = useHistory();
  const { search } = useLocation();
  const { open, enabled } = useTriageLauncher();
  // Guard so a re-render can't re-open / re-navigate / double-count after handled.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const sp = new URLSearchParams(search);
    const ctx = buildTriageContextFromParams(sp);

    AnalyticsUtils.captureCustomEvent('triage_deeplink_landed', {
      parse_ok: !!ctx,
      feature_enabled: enabled,
      param_style: paramStyle(sp),
      resource_type: ctx?.resourceType ?? null,
      resource_id: ctx?.resourceId ?? null,
      has_run_id: !!ctx?.runId,
      has_error_message: !!ctx?.errorSummary,
      has_timestamp: !!ctx?.ts,
      has_flow_id: !!ctx?.flowId,
      // The destination we redirect to — distinguishes a normal land from a
      // malformed-link fallback.
      redirected_to: ctx ? 'integrate_resource' : 'flows_listing',
    });

    if (ctx) {
      // open() is a no-op when the feature is disabled — the user still lands on
      // the flow, just without the modal (tracked as triage_modal_blocked).
      open(ctx, 'notification_deeplink');
      history.replace(integrateUrlForContext(ctx));
    } else {
      history.replace(ROUTE_FLOWS);
    }
  }, [search, open, enabled, history]);

  return null;
};

export default TriageLinkLanding;
